import { css } from '@emotion/react';
import { type FC } from 'react';
import {
  type DropdownOption,
  RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { NumberOfRangesDropdownComponent } from '~/boundary/settings/numberOfRanges/numberOfRangesDropdown.component';
import { useTheme } from '../../../../../../_shared/themes/theme.hooks';
import { type SpreadsheetColumnId } from '../../../../../../_shared/types/spreadsheetData/spreadsheetColumn';
import { type ThemeProps } from '../../../../../../_shared/types/themeProps';
import {
  type TranslationFnc, useTranslation,
} from '../../../../../../_shared/utils/hooks';
import {
  BoundaryTerritoryType,
  type CalculateBucketFunctionNumeric,
} from '../../../../../../store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { BoundarySelectedOption } from '../../fill/boundaryFill.enums';

const panelDropdownSectionStyle = css({
  marginBottom: 8,
});

const labelStyle = css({
  fontSize: '14px',
  marginBottom: '4px',
  textTransform: 'uppercase',
});

const dropdownTriggerStyle = ({ theme, isPlaceholder }: ThemeProps<{ isPlaceholder: boolean }>) => css({
  color: isPlaceholder ? theme.textColors.disabled : undefined,
  height: 40,
});

type ActiveBoundaryEditProps = Readonly<{
  boundaryFillData: SpreadsheetColumnId | null;
  boundaryTerritoryType: BoundaryTerritoryType | null;
  boundaryTerritoryTypeOptions: ReadonlyArray<DropdownOption<BoundaryTerritoryType>>;
  demographicCategoryOptions: ReadonlyArray<DropdownOption<string>>;
  demographicOptions: ReadonlyArray<DropdownOption<string>>;
  hideFillData: boolean;
  labelCountStart: number;
  numberOfRanges: number;
  numericCalculateBucketFunction: CalculateBucketFunctionNumeric | null;
  onBoundaryFillDataChange: (fillData: SpreadsheetColumnId) => void;
  onBoundaryTerritoryTypeChange: (type: BoundaryTerritoryType) => void;
  onDemographicIdChange: (demographicId: string) => void;
  onNumberOfRangesChange: ((range: number) => void) | undefined;
  onNumericCalculateBucketFunctionChange: (calculateFunction: CalculateBucketFunctionNumeric) => void;
  onSelectedDemographicCategoryChange: (category: string) => void;
  selectedDemographicCategory: string | null;
  selectedDemographicId: string | null;
  showDemographicGroup: boolean;
  spreadsheetColumnOptions: ReadonlyArray<DropdownOption<SpreadsheetColumnId>>;
}>;

export const ActiveBoundaryFillEditComponent: FC<ActiveBoundaryEditProps> = ({
  boundaryFillData,
  boundaryTerritoryType,
  boundaryTerritoryTypeOptions,
  demographicCategoryOptions,
  demographicOptions,
  hideFillData,
  labelCountStart,
  numberOfRanges,
  numericCalculateBucketFunction,
  onBoundaryFillDataChange,
  onBoundaryTerritoryTypeChange,
  onDemographicIdChange,
  onNumberOfRangesChange,
  onNumericCalculateBucketFunctionChange,
  onSelectedDemographicCategoryChange,
  selectedDemographicCategory,
  selectedDemographicId,
  showDemographicGroup,
  spreadsheetColumnOptions,
}) => {
  const [t] = useTranslation();
  const theme = useTheme();

  let labelCount = labelCountStart;

  return (
    <div>
      <div css={panelDropdownSectionStyle}>
        <div css={labelStyle}>
          {labelCount++}. {t(BoundarySelectedOption.BoundaryTerritoryType)}
        </div>

        <RegularDropdownComponent
          onChange={onBoundaryTerritoryTypeChange}
          options={boundaryTerritoryTypeOptions}
          placeholder={t(BoundarySelectedOption.BoundaryTerritoryType)}
          triggerStyle={dropdownTriggerStyle({ theme, isPlaceholder: boundaryTerritoryType === null })}
          value={boundaryTerritoryType}
          inPortal
        />
      </div>

      {boundaryTerritoryType && showDemographicGroup && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {labelCount++}. {t(BoundarySelectedOption.DemographicGroup)}
          </div>
          <RegularDropdownComponent
            onChange={onSelectedDemographicCategoryChange}
            options={demographicCategoryOptions}
            placeholder={t(BoundarySelectedOption.DemographicGroup)}
            triggerStyle={dropdownTriggerStyle({ theme, isPlaceholder: selectedDemographicCategory === null })}
            value={selectedDemographicCategory}
            inPortal
          />
        </div>
      )}

      {!hideFillData && showDemographicGroup && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {labelCount++}. {t(BoundarySelectedOption.BoundaryFillData)}
          </div>
          <RegularDropdownComponent
            onChange={onDemographicIdChange}
            options={demographicOptions}
            placeholder={t(BoundarySelectedOption.BoundaryFillData)}
            triggerStyle={dropdownTriggerStyle({ theme, isPlaceholder: onDemographicIdChange === null })}
            value={selectedDemographicId}
            inPortal
          />
        </div>
      )}

      {!hideFillData && boundaryTerritoryType === BoundaryTerritoryType.Numeric && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {labelCount++}. {t(BoundarySelectedOption.SelectCalculateFunction)}
          </div>

          <RegularDropdownComponent<CalculateBucketFunctionNumeric>
            onChange={onNumericCalculateBucketFunctionChange}
            options={getCalculateBucketFunctionOptions(t)}
            placeholder={t(BoundarySelectedOption.SelectCalculateFunction)}
            triggerStyle={dropdownTriggerStyle({ theme, isPlaceholder: !numericCalculateBucketFunction })}
            value={numericCalculateBucketFunction}
            inPortal
          />
        </div>
      )}

      {!hideFillData && !showDemographicGroup && (boundaryTerritoryType !== BoundaryTerritoryType.Numeric || numericCalculateBucketFunction) && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {labelCount++}. {t(BoundarySelectedOption.BoundaryFillData)}
          </div>

          <RegularDropdownComponent<SpreadsheetColumnId>
            onChange={onBoundaryFillDataChange}
            options={spreadsheetColumnOptions}
            placeholder={t(BoundarySelectedOption.BoundaryFillData)}
            triggerStyle={dropdownTriggerStyle({ theme, isPlaceholder: !boundaryFillData })}
            compareOptions={(a, b) => a?.columnId === b?.columnId && a?.spreadsheetId === b?.spreadsheetId}
            value={boundaryFillData}
            inPortal
          />
        </div>
      )}
      {onNumberOfRangesChange && (
        ((boundaryTerritoryType === BoundaryTerritoryType.Numeric) && boundaryFillData && numericCalculateBucketFunction && boundaryFillData.columnId) ||
        ((boundaryTerritoryType === BoundaryTerritoryType.Demographic) && selectedDemographicId) ||
        (boundaryTerritoryType === BoundaryTerritoryType.Density)
      ) && (
        <div css={panelDropdownSectionStyle}>
          <div css={labelStyle}>
            {labelCount++}. {t('Number Of Ranges')}
          </div>
          <NumberOfRangesDropdownComponent
            maxNumberOfRanges={10}
            numberOfRanges={numberOfRanges}
            onNumberOfRangesChange={onNumberOfRangesChange}
          />
        </div>
      )}
    </div>
  );
};

export const getCalculateBucketFunctionOptions = (
  t: TranslationFnc
): ReadonlyArray<DropdownOption<CalculateBucketFunctionNumeric>> => ([{
  name: t('Sum'),
  value: 'sum',
}, {
  name: t('Average'),
  value: 'avg',
}, {
  name: t('Min'),
  value: 'min',
}, {
  name: t('Max'),
  value: 'max',
}]);
